import Constants from "@/utils/constants";
import { supabasePlugin } from "@/plugins/supabase.ts";
import DBOperationsComb from "@/utils/dbOperationsComb";
import Exception from "@/utils/exception";

async function getPatrolFromFleetComplete(date, orderBy = ["timestamp"]) {
  try {
    let query = supabasePlugin.client_gps_tracker_client
      .from(Constants.enums.TABLES_NAME_ENUM.FLEET_COMPLETE_GPS_TABLE)
      .select("*")
      .eq("Date", date);

    // Handle multiple sort fields
    if (Array.isArray(orderBy)) {
      orderBy.forEach((field) => {
        if (typeof field === "string") {
          query = query.order(field, { ascending: false });
        }
      });
    }

    const { data, error } = await query;

    if (error) throw error;

    return {
      error: null,
      data: data || []
    };
  } catch (error) {
    return {
      error: error.message || "Failed to fetch patrol reports",
      data: []
    };
  }
}

async function getPatrolFromRoadWorks(
  date,
  orderBy = ["event_elapsedRealtimeNanos"]
) {
  try {
    const batchSize = 1000;
    let allData = [];
    let hasMore = true;
    let startIndex = 0;

    while (hasMore) {
      // Convert local date to UTC
      let startTime = new Date(date);
      let endTime = new Date(date);
      endTime.setHours(startTime.getHours() + 24);

      startTime = startTime.toISOString();
      endTime = endTime.toISOString();

      let query = supabasePlugin.client_gps_tracker_client
        .from(Constants.enums.TABLES_NAME_ENUM.CMA_519_USERS_LAT_LONG_TABLE)
        .select("latitude, longitude, timestamp, userDisplayName, deviceId")
        .gt("timestamp", startTime)
        .lt("timestamp", endTime)
        .range(startIndex, startIndex + batchSize - 1);

      // Handle multiple sort fields
      if (Array.isArray(orderBy)) {
        orderBy.forEach((field) => {
          if (typeof field === "string") {
            query = query.order(field, { ascending: false });
          }
        });
      }

      const { data, error } = await query;

      if (error) throw error;

      if (data && data.length > 0) {
        // formate the data
        const formattedData = data.map((item) => {
          return {
            Asset: `${item.userDisplayName}, ${item.deviceId}`,
            Date: item.timestamp.split("T")[0],
            Time: new Date(item.timestamp).toLocaleTimeString("en-US", {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit"
            }),
            Latitude: item.latitude,
            Longitude: item.longitude
          };
        });

        allData = [...allData, ...formattedData];
        startIndex += batchSize;
      } else {
        hasMore = false;
      }
    }

    return {
      error: null,
      data: allData
    };
  } catch (error) {
    console.log("Error:", error);
    return {
      error: error.message || "Failed to fetch patrol reports",
      data: []
    };
  }
}

async function getPatrolReportList(date, orderBy = ["timestamp"]) {
  try {
    const _res1 = await getPatrolFromFleetComplete(date, orderBy);
    console.log("Fleet complete data:", _res1.data.length);
    const _res2 = await getPatrolFromRoadWorks(date);
    console.log("Road works data:", _res2.data.length);
    return {
      error: null,
      data: [..._res1.data, ..._res2.data]
    };
  } catch (error) {
    Exception.catchException(error);
    throw error;
  }
}

async function getPatrolStatusReport(date, areaId = null, regionId = null) {
  try {
    const _res = await supabasePlugin.client_gps_tracker_client.rpc(
      Constants.enums.DATABASE_FUNCTION_ENUM.GET_PATROL_STATUS_REPORT,
      { week_date: date, p_area_ids: areaId, p_region_id: regionId }
    );
    return _res;
  } catch (error) {
    Exception.catchException(error);
    throw error;
  }
}

/** @param daytime - true = 7am–7pm only, false = whole day */
async function getPatrolStatusReportGPSData(
  highwayId,
  date,
  deviceId = null,
  truckId = null,
  daytime = true
) {
  try {
    const _res = await supabasePlugin.client_gps_tracker_client.rpc(
      Constants.enums.DATABASE_FUNCTION_ENUM.GET_WEEKLY_PATROL_REPORT_GPS_DATA,
      {
        p_highway_id: highwayId,
        p_patrol_date: date,
        p_device_id: deviceId,
        p_truck_id: truckId,
        p_daytime: daytime
      }
    );
    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function getPartialPatrolData(weekStartDate) {
  try {
    const weekEndDate = new Date(weekStartDate);
    // setting up end date to Saturday
    weekEndDate.setDate(weekEndDate.getDate() + 6);
    const _res = await supabasePlugin.client_gps_tracker_client
      .from(Constants.enums.TABLES_NAME_ENUM.PATROL_STATUS_TABLE)
      .select("*")
      .gte("patrolDate", weekStartDate.toISOString())
      .lte("patrolDate", weekEndDate.toISOString())
      .eq("status", Constants.enums.QC_STATUS_OPTIONS.QC_CHECK)
      .eq("is_deleted", false)
      .order("hwyUuid", { ascending: true })
      .order("patrolDate", { ascending: true })
      .order("coverage_percentage", { ascending: false });

    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

/** Fetch patrol status records for a single highway and date (any status), e.g. for patrolled view */
async function getPartialPatrolDataByHighwayAndDate(hwyUuid, date) {
  try {
    const d = new Date(date);
    const startOfDay = new Date(d);
    startOfDay.setUTCHours(0, 0, 0, 0);
    const endOfDay = new Date(d);
    endOfDay.setUTCHours(23, 59, 59, 999);
    const _res = await supabasePlugin.client_gps_tracker_client
      .from(Constants.enums.TABLES_NAME_ENUM.PATROL_STATUS_TABLE)
      .select("*")
      .eq("hwyUuid", hwyUuid)
      .gte("patrolDate", startOfDay.toISOString())
      .lte("patrolDate", endOfDay.toISOString())
      .eq("is_deleted", false)
      .order("patrolDate", { ascending: true })
      .order("coverage_percentage", { ascending: false });

    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function getWeeklyPatrolStatusReportQCData(config = {}) {
  try {
    const _res = await DBOperationsComb.fetchData(config, false);
    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function upsertWeeklyPatrolStatusReportQCData(data) {
  try {
    if (data.id) {
      return await DBOperationsComb.updateData(
        Constants.enums.TABLES_NAME_ENUM.PATROL_STATUS_REPORT_QC_DATA_TABLE,
        data,
        false
      );
    } else {
      return await DBOperationsComb.insertData(
        Constants.enums.TABLES_NAME_ENUM.PATROL_STATUS_REPORT_QC_DATA_TABLE,
        data,
        false
      );
    }
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function getWeeklyPatrolStatusReportQCHistory(config = {}) {
  try {
    const _res = await DBOperationsComb.fetchData(config, false);
    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function getWeeklyDefectsReport(weekStartDate, areaIds = null, regionId = null) {
  try {
    const statuses = Constants.enums.DEFECTS_STATUS;
    const statusesArray = [
      statuses.NEW,
      statuses.SUBMITTED,
      statuses.PENDING,
      statuses.ON_HOLD,
      statuses.IN_PROGRESS,
      statuses.DONE,
      statuses.QC_FAIL,
      statuses.QC_CHECK,
      statuses.IN_REVIEW,
      statuses.COMPLETED
    ];
    const _res = await DBOperationsComb.useSupabaseRPC(
      Constants.enums.DATABASE_FUNCTION_ENUM.GET_WEEKLY_DEFECTS_REPORT,
      {
        p_start_date: weekStartDate,
        p_end_date: null,
        p_defect_type_ids: null,
        p_statuses: statusesArray,
        p_priority: "1",
        p_area_ids: areaIds,
        p_region_id: regionId
      }
    );
    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

export default {
  getPatrolReportList,
  getPatrolFromRoadWorks,
  getPatrolStatusReport,
  getWeeklyPatrolStatusReportQCData,
  upsertWeeklyPatrolStatusReportQCData,
  getWeeklyPatrolStatusReportQCHistory,
  getPatrolStatusReportGPSData,
  getPartialPatrolData,
  getPartialPatrolDataByHighwayAndDate,
  getWeeklyDefectsReport
};
